import { z } from "zod";
import { type SubSlot } from "../breaker/form-factor.js";
import { type BreakerPlacement } from "../breaker/placement.js";
import type { FeederSource } from "../panel/feeder-source.js";
import {
  subPanelFed,
  subPanelUnlinked,
  utilityFed,
} from "../panel/feeder-source.js";
import type {
  HalfHeightEligibility,
  Orientation,
  PanelLayout,
  TandemEligibility,
} from "../panel/layout.js";
import { panelLayout } from "../panel/layout.js";
import type {
  CustomNumberingMap,
  NumberingScheme,
} from "../panel/numbering.js";
import { panel, type Panel } from "../panel/panel.js";
import type { Position } from "../panel/position.js";
import { position as makePosition } from "../panel/position.js";
import type { TandemIdScheme } from "../panel/tandem.js";
import type { PanelSafetyAcknowledgement } from "../safety/acknowledgement.js";
import { panelSafetyAcknowledgement } from "../safety/acknowledgement.js";
import { decodeBreaker, encodeBreaker } from "./breaker.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 6a codec for the panel-side aggregates: `Position`,
 * `PanelLayout` (including numbering / tandem-id / eligibility),
 * `BreakerPlacement`, `FeederSource`, and `Panel` itself. Installations
 * + circuits live in `circuit.ts` so the cross-aggregate boundary
 * stays clean.
 *
 * Determinism: key order matches the declaration order on the value
 * types; arrays preserve domain order (custom numbering by user-click
 * order, eligibility positions in input order); undefined optionals are
 * omitted. The smart factories `panelLayout` / `panel` re-canonicalize
 * on read.
 */

const positionJsonSchema = z.object({
  row: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
});

export type PositionJson = z.infer<typeof positionJsonSchema>;

export function encodePosition(p: Position): PositionJson {
  return { row: p.row, column: p.column };
}

export function decodePosition(j: PositionJson): Position {
  return makePosition(j.row, j.column);
}

const orientationSchema = z.enum(["vertical", "horizontal"]);
const tandemIdSchemeSchema = z.enum(["ab-suffix", "dot-suffix", "tb-suffix"]);

const numberingSchemeSchema: z.ZodType<NumberingScheme> = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("sequential-down-then-over") }),
    z.object({ kind: z.literal("odd-left-even-right") }),
    z.object({ kind: z.literal("sequential-left-then-down") }),
    z.object({
      kind: z.literal("custom"),
      mapping: z.array(
        z.object({
          position: positionJsonSchema,
          number: z.number().int().positive(),
        }),
      ),
    }),
  ],
);

const tandemEligibilitySchema: z.ZodType<TandemEligibility> =
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("all") }),
    z.object({ kind: z.literal("none") }),
    z.object({
      kind: z.literal("specific"),
      positions: z.array(positionJsonSchema),
    }),
  ]);

const halfHeightEligibilitySchema: z.ZodType<HalfHeightEligibility> =
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("all") }),
    z.object({ kind: z.literal("none") }),
    z.object({
      kind: z.literal("specific"),
      positions: z.array(positionJsonSchema),
    }),
  ]);

export const panelLayoutJsonSchema = z.object({
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  orientation: orientationSchema,
  numberingScheme: numberingSchemeSchema,
  tandemIdScheme: tandemIdSchemeSchema,
  tandemEligibility: tandemEligibilitySchema,
  halfHeightEligibility: halfHeightEligibilitySchema,
  splitBus: z.boolean(),
});

export type PanelLayoutJson = z.infer<typeof panelLayoutJsonSchema>;

function encodeNumberingScheme(s: NumberingScheme): Record<string, unknown> {
  switch (s.kind) {
    case "sequential-down-then-over":
    case "odd-left-even-right":
    case "sequential-left-then-down":
      return { kind: s.kind };
    case "custom": {
      const mapping = (s.mapping as CustomNumberingMap).map((entry) => ({
        position: encodePosition(entry.position),
        number: entry.number,
      }));
      return { kind: "custom", mapping };
    }
  }
}

function decodeNumberingScheme(
  j: z.infer<typeof numberingSchemeSchema>,
): NumberingScheme {
  switch (j.kind) {
    case "custom":
      return {
        kind: "custom",
        mapping: j.mapping.map((entry) => ({
          position: decodePosition(entry.position),
          number: entry.number,
        })),
      };
    default:
      return { kind: j.kind };
  }
}

function encodeTandemEligibility(e: TandemEligibility): Record<string, unknown> {
  switch (e.kind) {
    case "all":
    case "none":
      return { kind: e.kind };
    case "specific":
      return {
        kind: "specific",
        positions: e.positions.map(encodePosition),
      };
  }
}

function decodeTandemEligibility(
  j: z.infer<typeof tandemEligibilitySchema>,
): TandemEligibility {
  switch (j.kind) {
    case "specific":
      return { kind: "specific", positions: j.positions.map(decodePosition) };
    default:
      return { kind: j.kind };
  }
}

function encodeHalfHeightEligibility(
  e: HalfHeightEligibility,
): Record<string, unknown> {
  switch (e.kind) {
    case "all":
    case "none":
      return { kind: e.kind };
    case "specific":
      return {
        kind: "specific",
        positions: e.positions.map(encodePosition),
      };
  }
}

function decodeHalfHeightEligibility(
  j: z.infer<typeof halfHeightEligibilitySchema>,
): HalfHeightEligibility {
  switch (j.kind) {
    case "specific":
      return { kind: "specific", positions: j.positions.map(decodePosition) };
    default:
      return { kind: j.kind };
  }
}

export function encodePanelLayout(layout: PanelLayout): Record<string, unknown> {
  return {
    columns: layout.columns,
    rows: layout.rows,
    orientation: layout.orientation satisfies Orientation,
    numberingScheme: encodeNumberingScheme(layout.numberingScheme),
    tandemIdScheme: layout.tandemIdScheme satisfies TandemIdScheme,
    tandemEligibility: encodeTandemEligibility(layout.tandemEligibility),
    halfHeightEligibility: encodeHalfHeightEligibility(
      layout.halfHeightEligibility,
    ),
    splitBus: layout.splitBus,
  };
}

export function decodePanelLayout(input: unknown): PanelLayout {
  const parsed = panelLayoutJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodePanelLayout: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return panelLayout({
    columns: j.columns,
    rows: j.rows,
    orientation: j.orientation,
    numberingScheme: decodeNumberingScheme(j.numberingScheme),
    tandemIdScheme: j.tandemIdScheme,
    tandemEligibility: decodeTandemEligibility(j.tandemEligibility),
    halfHeightEligibility: decodeHalfHeightEligibility(j.halfHeightEligibility),
    splitBus: j.splitBus,
  });
}

const subSlotSchema: z.ZodType<SubSlot> = z.enum(["upper", "lower"]);

export const breakerPlacementJsonSchema = z.object({
  breaker: z.unknown(),
  origin: positionJsonSchema,
  subSlot: subSlotSchema.optional(),
});

export type BreakerPlacementJson = z.infer<typeof breakerPlacementJsonSchema>;

export function encodeBreakerPlacement(
  p: BreakerPlacement,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    breaker: encodeBreaker(p.breaker),
    origin: encodePosition(p.origin),
  };
  if (p.subSlot !== undefined) out["subSlot"] = p.subSlot;
  return out;
}

export function decodeBreakerPlacement(input: unknown): BreakerPlacement {
  const parsed = breakerPlacementJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeBreakerPlacement: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  const breaker = decodeBreaker(j.breaker);
  if (j.subSlot !== undefined) {
    return {
      breaker,
      origin: decodePosition(j.origin),
      subSlot: j.subSlot,
    };
  }
  return { breaker, origin: decodePosition(j.origin) };
}

export const feederSourceJsonSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("utility") }),
  z.object({
    kind: z.literal("sub-panel"),
    parentCircuitId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("sub-panel-unlinked"),
    lastKnownParentCircuitId: z.string().min(1).optional(),
  }),
]);

export function encodeFeederSource(f: FeederSource): Record<string, unknown> {
  switch (f.kind) {
    case "utility":
      return { kind: "utility" };
    case "sub-panel":
      return { kind: "sub-panel", parentCircuitId: f.parentCircuitId };
    case "sub-panel-unlinked": {
      const out: Record<string, unknown> = { kind: "sub-panel-unlinked" };
      if (f.lastKnownParentCircuitId !== undefined) {
        out["lastKnownParentCircuitId"] = f.lastKnownParentCircuitId;
      }
      return out;
    }
  }
}

export function decodeFeederSource(input: unknown): FeederSource {
  const parsed = feederSourceJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeFeederSource: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  switch (j.kind) {
    case "utility":
      return utilityFed();
    case "sub-panel":
      return subPanelFed(j.parentCircuitId);
    case "sub-panel-unlinked":
      return subPanelUnlinked(j.lastKnownParentCircuitId);
  }
}

export const panelSafetyAcknowledgementJsonSchema = z.object({
  entryId: z.string().min(1),
  acknowledgedAt: instantSchema,
  note: z.string().optional(),
});

export type PanelSafetyAcknowledgementJson = z.infer<
  typeof panelSafetyAcknowledgementJsonSchema
>;

export function encodePanelSafetyAcknowledgement(
  ack: PanelSafetyAcknowledgement,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    entryId: ack.entryId,
    acknowledgedAt: ack.acknowledgedAt,
  };
  if (ack.note !== undefined) out["note"] = ack.note;
  return out;
}

export function decodePanelSafetyAcknowledgement(
  input: unknown,
): PanelSafetyAcknowledgement {
  const parsed = panelSafetyAcknowledgementJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodePanelSafetyAcknowledgement: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return panelSafetyAcknowledgement({
    entryId: j.entryId,
    acknowledgedAt: j.acknowledgedAt,
    ...(j.note !== undefined ? { note: j.note } : {}),
  });
}

export const panelJsonSchema = z.object({
  id: z.string().min(1),
  layout: panelLayoutJsonSchema,
  installations: z.array(z.unknown()),
  name: z.string().min(1).optional(),
  manufacturer: z.string().optional(),
  series: z.string().optional(),
  model: z.string().optional(),
  mainAmperage: z.number().int().positive().optional(),
  feederSource: feederSourceJsonSchema,
  lastLegendGeneratedAt: instantSchema.optional(),
  lastLegendHash: z.string().min(1).optional(),
  safetyAcknowledgement: panelSafetyAcknowledgementJsonSchema.optional(),
});

export type PanelJson = z.infer<typeof panelJsonSchema>;

/**
 * `encodePanel` — declared key order: id, layout, installations,
 * optional metadata fields (manufacturer, model, mainAmperage),
 * feederSource. The `installations` encoder is supplied by
 * `circuit.ts` to break the cross-import cycle with the installation /
 * circuit value types.
 */
export function encodePanel(
  p: Panel,
  encodeInstallation: (i: Panel["installations"][number]) => unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: p.id,
    layout: encodePanelLayout(p.layout),
    installations: p.installations.map(encodeInstallation),
  };
  if (p.name !== undefined) out["name"] = p.name;
  if (p.manufacturer !== undefined) out["manufacturer"] = p.manufacturer;
  if (p.series !== undefined) out["series"] = p.series;
  if (p.model !== undefined) out["model"] = p.model;
  if (p.mainAmperage !== undefined) out["mainAmperage"] = p.mainAmperage;
  out["feederSource"] = encodeFeederSource(p.feederSource);
  if (p.lastLegendGeneratedAt !== undefined) {
    out["lastLegendGeneratedAt"] = p.lastLegendGeneratedAt;
  }
  if (p.lastLegendHash !== undefined) {
    out["lastLegendHash"] = p.lastLegendHash;
  }
  if (p.safetyAcknowledgement !== undefined) {
    out["safetyAcknowledgement"] = encodePanelSafetyAcknowledgement(
      p.safetyAcknowledgement,
    );
  }
  return out;
}

/**
 * `decodePanel` — runs `panelJsonSchema` over the input frame, then
 * dispatches the per-installation decoder (supplied by `circuit.ts`)
 * and reassembles through the `panel` smart factory so all
 * placement / conflict invariants are re-checked.
 */
export function decodePanel(
  input: unknown,
  decodeInstallation: (i: unknown) => Panel["installations"][number],
): Panel {
  const parsed = panelJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodePanel: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  const layout = decodePanelLayout(j.layout);
  const installations = j.installations.map(decodeInstallation);
  const feederSource = decodeFeederSource(j.feederSource);
  return panel({
    id: j.id,
    layout,
    installations,
    ...(j.name !== undefined ? { name: j.name } : {}),
    ...(j.manufacturer !== undefined ? { manufacturer: j.manufacturer } : {}),
    ...(j.series !== undefined ? { series: j.series } : {}),
    ...(j.model !== undefined ? { model: j.model } : {}),
    ...(j.mainAmperage !== undefined ? { mainAmperage: j.mainAmperage } : {}),
    feederSource,
    ...(j.lastLegendGeneratedAt !== undefined
      ? { lastLegendGeneratedAt: j.lastLegendGeneratedAt }
      : {}),
    ...(j.lastLegendHash !== undefined ? { lastLegendHash: j.lastLegendHash } : {}),
    ...(j.safetyAcknowledgement !== undefined
      ? {
          safetyAcknowledgement: decodePanelSafetyAcknowledgement(
            j.safetyAcknowledgement,
          ),
        }
      : {}),
  });
}
